import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, FileSpreadsheet, Users, CheckCircle, Clock, ChevronDown, ChevronRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

interface SurveyResultsProps {
  companyId: string;
}

interface SurveyIntervention {
  id: string;
  title: string;
  message: string | null;
  content: {
    questions?: Array<{
      question: string;
      type: 'text' | 'rating' | 'multiple_choice';
      options?: string[];
    }>;
  };
  status: string;
  created_at: string;
  target_type: string;
  completed_at: string | null;
  outcome_data: {
    responses?: Record<string, any>;
  };
}

interface ResponseData {
  worker_name: string;
  completed_at: string;
  responses: Record<string, any>;
}

const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

export default function SurveyResults({ companyId }: SurveyResultsProps) {
  const [surveys, setSurveys] = useState<SurveyIntervention[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedSurvey, setExpandedSurvey] = useState<string | null>(null);
  const [surveyResponses, setSurveyResponses] = useState<Record<string, ResponseData[]>>({});

  useEffect(() => {
    loadSurveys();
  }, [companyId]);

  const loadSurveys = async () => {
    try {
      const { data, error } = await supabase
        .from('interventions')
        .select('*')
        .eq('company_id', companyId)
        .eq('category', 'survey')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSurveys((data || []) as SurveyIntervention[]);
    } catch (error) {
      console.error('Error loading surveys:', error);
      toast.error('Failed to load surveys');
    } finally {
      setLoading(false);
    }
  };

  const loadSurveyResponses = async (surveyId: string) => {
    try {
      // Load survey responses from survey_responses table
      const { data: responsesData, error } = await supabase
        .from('survey_responses')
        .select('id, worker_id, responses, completed_at')
        .eq('survey_template_id', surveyId);

      if (error) {
        // Fallback: try worker_quests if survey_responses doesn't have data
        const { data: quests } = await supabase
          .from('worker_quests')
          .select('id, worker_id, status, completed_at, response_data')
          .eq('status', 'completed');
        
        // Get worker names
        const workerIds = (quests || []).map((q: any) => q.worker_id);
        const { data: workers } = await supabase
          .from('worker_profiles')
          .select('id, display_name')
          .in('id', workerIds);
        
        const workerMap = new Map((workers || []).map((w: any) => [w.id, w.display_name]));
        
        const responses: ResponseData[] = (quests || []).map((q: any) => ({
          worker_name: workerMap.get(q.worker_id) || 'Unknown',
          completed_at: q.completed_at || new Date().toISOString(),
          responses: q.response_data || {}
        }));

        setSurveyResponses(prev => ({ ...prev, [surveyId]: responses }));
        return;
      }

      // Get worker names for responses
      const workerIds = (responsesData || []).map((r: any) => r.worker_id);
      const { data: workers } = await supabase
        .from('worker_profiles')
        .select('id, display_name')
        .in('id', workerIds);
      
      const workerMap = new Map((workers || []).map((w: any) => [w.id, w.display_name]));

      const responses: ResponseData[] = (responsesData || []).map((r: any) => ({
        worker_name: workerMap.get(r.worker_id) || 'Unknown',
        completed_at: r.completed_at || new Date().toISOString(),
        responses: r.responses || {}
      }));

      setSurveyResponses(prev => ({ ...prev, [surveyId]: responses }));
    } catch (error) {
      console.error('Error loading responses:', error);
    }
  };

  const toggleSurvey = (surveyId: string) => {
    if (expandedSurvey === surveyId) {
      setExpandedSurvey(null);
    } else {
      setExpandedSurvey(surveyId);
      if (!surveyResponses[surveyId]) {
        loadSurveyResponses(surveyId);
      }
    }
  };

  const exportToCSV = (survey: SurveyIntervention) => {
    const responses = surveyResponses[survey.id] || [];
    if (responses.length === 0) {
      toast.error('No responses to export');
      return;
    }

    const questions = survey.content?.questions || [];
    const headers = ['Worker', 'Completed At', ...questions.map(q => q.question)];
    
    const rows = responses.map(r => [
      r.worker_name,
      new Date(r.completed_at).toLocaleString('id-ID'),
      ...questions.map((_, idx) => r.responses[`q${idx}`] || '-')
    ]);

    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `survey_${survey.id}_responses.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported successfully');
  };

  const getResponseStats = (survey: SurveyIntervention, questionIdx: number) => {
    const responses = surveyResponses[survey.id] || [];
    const question = survey.content?.questions?.[questionIdx];
    
    if (!question || question.type !== 'multiple_choice') return null;

    const optionCounts: Record<string, number> = {};
    (question.options || []).forEach(opt => { optionCounts[opt] = 0; });

    responses.forEach(r => {
      const answer = r.responses[`q${questionIdx}`];
      if (answer && optionCounts[answer] !== undefined) {
        optionCounts[answer]++;
      }
    });

    return Object.entries(optionCounts).map(([name, value]) => ({ name, value }));
  };

  const completedSurveys = surveys.filter(s => s.status === 'completed');
  const activeSurveys = surveys.filter(s => s.status !== 'completed');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="h-5 w-5 text-blue-500" />
            <div>
              <p className="text-2xl font-bold">{surveys.length}</p>
              <p className="text-sm text-muted-foreground">Total Surveys</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5 text-amber-500" />
            <div>
              <p className="text-2xl font-bold">{activeSurveys.length}</p>
              <p className="text-sm text-muted-foreground">Active</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <div>
              <p className="text-2xl font-bold">{completedSurveys.length}</p>
              <p className="text-sm text-muted-foreground">Completed</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Survey List */}
      <div className="space-y-3">
        {surveys.length === 0 ? (
          <Card className="p-8 text-center">
            <FileSpreadsheet className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No surveys created yet</p>
          </Card>
        ) : (
          surveys.map(survey => (
            <Card key={survey.id} className="overflow-hidden">
              <div 
                className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => toggleSurvey(survey.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {expandedSurvey === survey.id ? (
                      <ChevronDown className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    )}
                    <div>
                      <h3 className="font-semibold">{survey.title}</h3>
                      <p className="text-sm text-muted-foreground">
                        {new Date(survey.created_at).toLocaleDateString('id-ID')} • {survey.content?.questions?.length || 0} questions
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={survey.status === 'completed' ? 'default' : 'secondary'}>
                      {survey.status}
                    </Badge>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={(e) => { e.stopPropagation(); exportToCSV(survey); }}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      CSV
                    </Button>
                  </div>
                </div>
              </div>

              <AnimatePresence>
                {expandedSurvey === survey.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="border-t"
                  >
                    <div className="p-4 space-y-4">
                      {/* Response Count */}
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Users className="h-4 w-4" />
                        {surveyResponses[survey.id]?.length || 0} responses
                      </div>

                      {/* Question Results */}
                      {survey.content?.questions?.map((question, idx) => (
                        <div key={idx} className="space-y-2">
                          <p className="font-medium text-sm">Q{idx + 1}: {question.question}</p>
                          
                          {question.type === 'multiple_choice' && getResponseStats(survey, idx) && (
                            <div className="h-48">
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={getResponseStats(survey, idx)!}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="name" fontSize={12} />
                                  <YAxis />
                                  <Tooltip />
                                  <Bar dataKey="value" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                                </BarChart>
                              </ResponsiveContainer>
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Raw Responses Table */}
                      {surveyResponses[survey.id] && surveyResponses[survey.id].length > 0 && (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-muted/50">
                              <tr>
                                <th className="p-2 text-left">Worker</th>
                                <th className="p-2 text-left">Completed</th>
                                {survey.content?.questions?.map((_, idx) => (
                                  <th key={idx} className="p-2 text-left">Q{idx + 1}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {surveyResponses[survey.id].map((resp, idx) => (
                                <tr key={idx} className="border-t">
                                  <td className="p-2">{resp.worker_name}</td>
                                  <td className="p-2">{new Date(resp.completed_at).toLocaleString('id-ID')}</td>
                                  {survey.content?.questions?.map((_, qIdx) => (
                                    <td key={qIdx} className="p-2">{resp.responses[`q${qIdx}`] || '-'}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
